import * as Sentry from '@sentry/remix';
import {
  replayIntegration,
  thirdPartyErrorFilterIntegration
} from '@sentry/remix';
import type { Resource } from 'i18next';
import i18next from 'i18next';
import Backend from 'i18next-http-backend';
import { startTransition, StrictMode, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { useLocation, useMatches } from 'react-router';
import { HydratedRouter } from 'react-router/dom';
import { getInitialNamespaces } from 'remix-i18next/client';

import i18n from './i18n';
import { getRegion } from './utils/region';

function initSentry() {
  Sentry.init({
    dsn: 'https://dc8be4a7b901297a2ac962b6ca3428b8@o308939.ingest.us.sentry.io/4506814042800128',
    enabled: process.env.NODE_ENV === 'production',
    environment: process.env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches
      }),
      replayIntegration(),
      thirdPartyErrorFilterIntegration({
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        filterKeys: ['tss-bolt-app']
      })
    ],
    replaysOnErrorSampleRate: 1,
    replaysSessionSampleRate: 0,
    tracesSampleRate: 1
  });
}

async function initi18n() {
  const region = getRegion(new URL(location.href));

  const resources = (window as unknown as { __i18n?: unknown }).__i18n;

  const instance = i18next.use(initReactI18next);

  if (resources) {
    return instance.init({
      ...i18n,
      fallbackLng: 'en',
      lng: region.lang,
      ns: getInitialNamespaces(),
      resources: resources as Resource
    });
  }

  return instance.use(Backend).init({
    ...i18n,
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      requestOptions: {
        cache: 'no-store'
      }
    },
    fallbackLng: 'en',
    lng: region.lang,
    ns: getInitialNamespaces()
  });
}

async function hydrate() {
  initSentry();
  await initi18n();

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <HydratedRouter />
        </StrictMode>
      </I18nextProvider>
    );
  });
}

hydrate();
